/**
 * Capacity & Trust Scores Dashboard
 * Shows provider performance metrics based on matched and accepted referrals
 * Updated: 2025-10-17 - Enhanced with Google Places, filters, export, detail view
 */

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, BarChart3, Search, Star, Users, TrendingUp, Target, Clock, CheckCircle, Building, MapPin, Download, Calendar, RefreshCw, Phone, Mail, Globe, Shield, AlertCircle } from "lucide-react";
import { config } from "@/src/config";
import { googlePlacesService } from "@/services/googlePlacesApi";
import { ProviderDetailModal } from "./ProviderDetailModal";
import { authFetch } from '@/lib/authFetch';

interface ProviderMetrics {
  id: string;
  name: string;
  organizationType: string;
  zipCode: string;
  city?: string;
  state?: string;
  services: string[];
  referralsAccepted: number;
  referralsMatched: number;
  avgMatchToCloseTime: number; // in days
  dataQualityScore: number; // 0-100
  googleRating: number | null; // 1-5 or null if not available
  googleReviewCount: number;
  phoneNumber?: string;
  email?: string;
  website?: string;
  streetAddress?: string;

  // Acceptance workflow metrics (PHI Integration)
  referralsPending?: number;        // Awaiting acceptance
  referralsRejected?: number;       // Rejected by provider
  referralsExpired?: number;        // Expired without response
  acceptanceRate?: number;          // Percentage accepted
  avgAcceptanceTime?: number;       // Hours to accept
  phiEmailsSent?: number;           // Secure PHI emails sent
}

interface CapacityDashboardProps {
  onBack?: () => void;
}

export function CapacityDashboard({ onBack }: CapacityDashboardProps) {
  const [providers, setProviders] = useState<ProviderMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<ProviderMetrics | null>(null);
  const [dateRange, setDateRange] = useState<'all' | '30days' | '90days' | '6months' | '1year'>('all');
  const [fetchingRatings, setFetchingRatings] = useState(false);

  // Fetch matched referrals and calculate provider metrics
  useEffect(() => {
    console.log('🔥 CAPACITY DASHBOARD LOADED - VERSION 2.0 - MOCK DATA REMOVED');
    console.log('🔥 Timestamp:', new Date().toISOString());
    fetchProviderMetrics();
  }, []);

  const fetchProviderMetrics = async () => {
    try {
      setLoading(true);

      // Fetch all referrals with cache-busting via query parameter
      // Note: Cache-Control headers removed to avoid CORS preflight issues
      const response = await authFetch(`${config.api.baseUrl}/api/referrals?t=${Date.now()}`, {
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch referrals');
      }

      const data = await response.json();
      // Backend returns array directly, not wrapped in {referrals: []}
      const referrals = Array.isArray(data) ? data : (data.referrals || []);

      console.log('📋 All referrals fetched:', referrals.length);
      console.log('📋 Referral statuses:', referrals.map((r: any) => ({ id: r.id, status: r.status })));

      // CRITICAL DEBUG: Show we're using real data, not mock data
      if (referrals.length === 0) {
        console.warn('⚠️ NO REFERRALS FOUND IN DATABASE - Will show empty state');
      }

      // Filter matched and accepted referrals (includes both manual and auto-matched)
      const matchedReferrals = referrals.filter((ref: any) =>
        ref.status === 'Matched' ||
        ref.status === 'Auto-Matched' ||  // Auto-matched referrals sent to multiple providers
        ref.status === 'Accepted' ||
        ref.status === 'In Progress' ||
        ref.status === 'Completed'
      );

      console.log('✅ Matched referrals:', matchedReferrals.length);
      console.log('✅ Matched referral details:', matchedReferrals.map((r: any) => ({
        id: r.id,
        status: r.status,
        matched_provider_id: r.matched_provider_id,
        matched_provider_name: r.matched_provider_name,
        matched_provider_organization: r.matched_provider_organization
      })));

      // Group by provider and calculate metrics
      const providerMap = new Map<string, any>();

      matchedReferrals.forEach((referral: any) => {
        // Handle both manual matched (single provider) and auto-matched (multiple providers)
        let providersToProcess = [];

        if (referral.status === 'Auto-Matched' && referral.matched_providers) {
          // Auto-matched referral - parse JSON array of providers
          try {
            const matchedProviders = typeof referral.matched_providers === 'string'
              ? JSON.parse(referral.matched_providers)
              : referral.matched_providers;

            providersToProcess = matchedProviders.map((p: any) => ({
              providerId: p.provider_id || p.id || 'unknown',
              providerName: p.provider_name || p.organization_name || 'Unknown Provider',
              providerOrg: p.provider_organization || p.organization_type || 'Community-Based Organization',
              status: p.status || 'sent'  // Auto-matched providers have individual status
            }));

            console.log('📊 Processing auto-matched referral:', {
              referralId: referral.id,
              providerCount: providersToProcess.length,
              status: referral.status
            });
          } catch (e) {
            console.error('Error parsing matched_providers:', e);
          }
        } else {
          // Manually matched referral - single provider
          const providerId = referral.matched_provider_id || referral.cbo_id || 'unknown';
          const providerName = referral.matched_provider_name || referral.cbo_name || 'Unknown Provider';
          const providerOrg = referral.matched_provider_organization || referral.provider_type || 'Community-Based Organization';

          providersToProcess = [{
            providerId,
            providerName,
            providerOrg,
            status: referral.status
          }];

          console.log('📊 Processing manually matched referral:', {
            referralId: referral.id,
            providerId,
            providerName,
            providerOrg,
            status: referral.status
          });
        }

        // Process each provider
        providersToProcess.forEach(({ providerId, providerName, providerOrg, status }) => {

        if (!providerMap.has(providerId)) {
          providerMap.set(providerId, {
            id: providerId,
            name: providerName,
            organizationType: providerOrg,
            zipCode: '', // Will be fetched from provider directory
            city: '', // Will be fetched from provider directory
            state: '', // Will be fetched from provider directory
            services: new Set<string>(),
            referralsAccepted: 0,
            referralsMatched: 0,
            totalMatchToCloseTime: 0,
            completedReferrals: 0,
            dataQualityScore: 85, // Default, will be updated from provider directory
            googleRating: null,
            googleReviewCount: 0,
            phoneNumber: '',
            email: '',
            website: '',
            streetAddress: '',
          });
        }

        const provider = providerMap.get(providerId);

        // Add services
        if (referral.sdoh_needs) {
          const needs = Array.isArray(referral.sdoh_needs) ? referral.sdoh_needs : [referral.sdoh_needs];
          needs.forEach((need: string) => provider.services.add(need));
        }

        // Count referrals - Use individual provider status for auto-matched, referral status for manual
        // For auto-matched: status can be 'sent', 'accepted', 'in_progress', 'completed'
        // For manual: status is 'Matched', 'Accepted', 'In Progress', 'Completed'
        const providerStatus = status.toLowerCase();

        if (providerStatus === 'matched' || providerStatus === 'sent' ||
            providerStatus === 'accepted' || providerStatus === 'in progress' ||
            providerStatus === 'in_progress' || providerStatus === 'completed') {
          provider.referralsMatched++;
        }

        if (providerStatus === 'accepted' || providerStatus === 'in progress' ||
            providerStatus === 'in_progress' || providerStatus === 'completed') {
          provider.referralsAccepted++;
        }

        // Calculate match to close time for completed referrals
        // Use matched_at (when referral was matched to provider) to updated_at (when closed)
        if (providerStatus === 'completed' && referral.matched_at && referral.updated_at) {
          const matchDate = new Date(referral.matched_at);
          const closeDate = new Date(referral.updated_at);
          const daysDiff = Math.floor((closeDate.getTime() - matchDate.getTime()) / (1000 * 60 * 60 * 24));
          provider.totalMatchToCloseTime += daysDiff;
          provider.completedReferrals++;
        }
        }); // Close providersToProcess.forEach
      }); // Close matchedReferrals.forEach

      // Convert to array and calculate averages
      const providerMetrics: ProviderMetrics[] = Array.from(providerMap.values()).map(provider => ({
        ...provider,
        services: Array.from(provider.services),
        avgMatchToCloseTime: provider.completedReferrals > 0
          ? Math.round(provider.totalMatchToCloseTime / provider.completedReferrals)
          : 0,
      }));

      // Sort by number of referrals accepted (descending)
      providerMetrics.sort((a, b) => b.referralsAccepted - a.referralsAccepted);

      console.log('🎯 Final provider metrics:', providerMetrics);
      console.log('🎯 Provider count:', providerMetrics.length);

      // CRITICAL DEBUG: Confirm we're setting real data
      console.log('✅ Setting REAL provider data (not mock):', {
        providerCount: providerMetrics.length,
        totalAccepted: providerMetrics.reduce((sum, p) => sum + p.referralsAccepted, 0),
        totalMatched: providerMetrics.reduce((sum, p) => sum + p.referralsMatched, 0)
      });

      // Set providers immediately to show data
      setProviders(providerMetrics);

      // Fetch provider details from saved_providers table (My Providers)
      // This gives us complete address and contact information
      try {
        // Fetch saved providers once with cache-busting via query parameter
        const response = await authFetch(`${config.api.baseUrl}/api/saved-providers?t=${Date.now()}`, {
          cache: 'no-store'
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`📦 Saved providers API response:`, data);

          // Handle different response formats
          const savedProvidersList = Array.isArray(data) ? data : (data.saved_providers || data.providers || []);

          console.log(`📋 Saved providers list (${savedProvidersList.length} providers):`, savedProvidersList);

          // Enrich each provider with saved provider data
          const enrichedProviders = providerMetrics.map((provider) => {
            // Find provider by ID first, then by normalized name
            const normalize = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            const matchedProvider = savedProvidersList.find((sp: any) =>
              (sp.provider_id && provider.id && sp.provider_id === provider.id) ||
              normalize(sp.organization_name) === normalize(provider.name)
            );

            if (matchedProvider) {
              console.log(`✅ Found saved provider details for ${provider.name}:`, matchedProvider);
              return {
                ...provider,
                zipCode: matchedProvider.zip_code || provider.zipCode,
                city: matchedProvider.city || provider.city,
                state: matchedProvider.state || provider.state,
                phoneNumber: matchedProvider.primary_phone || provider.phoneNumber,
                email: matchedProvider.primary_email || provider.email,
                website: matchedProvider.website || provider.website,
                streetAddress: matchedProvider.street_address || provider.streetAddress,
                dataQualityScore: matchedProvider.data_quality_score || provider.dataQualityScore,
              };
            } else {
              return provider;
            }
          });

          // Update with enriched data if available
          enrichedProviders.sort((a, b) => b.referralsAccepted - a.referralsAccepted);
          console.log('✨ Enriched provider metrics with saved provider data:', enrichedProviders);
          setProviders(enrichedProviders);
        } else {
          console.warn('Failed to fetch saved providers, keeping original data');
        }
      } catch (error) {
        console.warn('Could not enrich provider data from saved providers:', error);
        // Keep the original data without enrichment
      }
    } catch (error) {
      console.error('Error fetching provider metrics:', error);
      // Don't use mock data - show empty state if there's an error
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter providers based on search term
  const filteredProviders = providers.filter(provider => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      provider.name.toLowerCase().includes(searchLower) ||
      provider.zipCode.includes(searchTerm) ||
      provider.city?.toLowerCase().includes(searchLower) ||
      provider.state?.toLowerCase().includes(searchLower) ||
      provider.organizationType.toLowerCase().includes(searchLower) ||
      provider.services.some(service => service.toLowerCase().includes(searchLower))
    );
  });

  // Calculate summary metrics
  // Count providers that have been matched OR accepted (not just accepted)
  const providersWithReferrals = filteredProviders.filter(p => p.referralsMatched > 0 || p.referralsAccepted > 0);
  const totalProviders = providersWithReferrals.length;

  console.log('📊 Metrics Calculation:', {
    totalFilteredProviders: filteredProviders.length,
    providersWithReferrals: providersWithReferrals.length,
    providerNames: providersWithReferrals.map(p => p.name)
  });

  // Calculate average data quality only for providers with matched/accepted referrals
  const avgDataQuality = totalProviders > 0
    ? Math.round(providersWithReferrals.reduce((sum, p) => sum + p.dataQualityScore, 0) / totalProviders)
    : 0;
  // Calculate average Google rating only for providers with referrals AND ratings
  const providersWithRatings = providersWithReferrals.filter(p => p.googleRating !== null);
  const avgGoogleRating = providersWithRatings.length > 0
    ? (providersWithRatings.reduce((sum, p) => sum + (p.googleRating || 0), 0) / providersWithRatings.length).toFixed(1)
    : 'N/A';
  const totalReferrals = filteredProviders.reduce((sum, p) => sum + p.referralsAccepted + p.referralsMatched, 0);

  const renderStars = (rating: number | null) => {
    if (rating === null) {
      return <span className="text-base text-gray-400 font-medium">N/A</span>;
    }

    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex flex-col items-center">
        <div className="flex items-center space-x-0.5 mb-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-5 h-5 ${
                i < fullStars
                  ? "text-yellow-400 fill-yellow-400"
                  : i === fullStars && hasHalfStar
                  ? "text-yellow-300 fill-yellow-300"
                  : "text-gray-300 fill-gray-300"
              }`}
            />
          ))}
        </div>
        <span className="text-lg font-bold text-gray-800">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const getDataQualityColor = (score: number) => {
    if (score >= 90) return "text-green-600";
    if (score >= 75) return "text-blue-600";
    if (score >= 60) return "text-yellow-600";
    return "text-orange-600";
  };

  const getPerformanceColor = (days: number) => {
    if (days === 0 || !days) return "text-gray-500";
    if (days <= 10) return "text-green-600";
    if (days <= 20) return "text-blue-600";
    if (days <= 30) return "text-yellow-600";
    return "text-orange-600";
  };

  // CSV Export functionality
  const exportToCSV = () => {
    const headers = [
      'Provider Name',
      'Organization Type',
      'City',
      'State',
      'ZIP',
      'Referrals Accepted',
      'Referrals Matched',
      'Avg Match-to-Close Time (days)',
      'Data Quality Score',
      'Google Rating',
      'Google Review Count',
      'Services'
    ];

    const rows = filteredProviders.map(provider => [
      provider.name,
      provider.organizationType,
      provider.city || '',
      provider.state || '',
      provider.zipCode,
      provider.referralsAccepted,
      provider.referralsMatched,
      provider.avgMatchToCloseTime,
      provider.dataQualityScore,
      provider.googleRating || 'N/A',
      provider.googleReviewCount,
      provider.services.join('; ')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `provider-metrics-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Fetch Google Places ratings for all providers
  const fetchGoogleRatings = async () => {
    setFetchingRatings(true);
    try {
      // Filter providers that have location data
      const providersWithLocation = providers.filter(p => p.city && p.state && p.zipCode);

      if (providersWithLocation.length === 0) {
        console.warn('No providers with complete location data found');
        alert('Unable to fetch ratings: Provider location data is incomplete. This will be populated from the provider directory.');
        setFetchingRatings(false);
        return;
      }

      const providersToFetch = providersWithLocation.map(p => ({
        name: p.name,
        address: `${p.city}, ${p.state} ${p.zipCode}`
      }));

      console.log('🔍 Fetching Google ratings for providers:', providersToFetch);
      const ratings = await googlePlacesService.batchGetRatings(providersToFetch);
      console.log('⭐ Ratings fetched:', ratings);

      const updatedProviders = providers.map(provider => {
        const rating = ratings.get(provider.name);
        if (rating) {
          console.log(`✅ Updated rating for ${provider.name}:`, rating);
          return {
            ...provider,
            googleRating: rating.rating,
            googleReviewCount: rating.userRatingsTotal
          };
        }
        return provider;
      });

      setProviders(updatedProviders);

      const updatedCount = updatedProviders.filter(p => p.googleRating !== null).length;
      if (updatedCount > 0) {
        alert(`Successfully updated Google ratings for ${updatedCount} provider(s)!`);
      } else {
        alert('No Google ratings found for the providers. They may not be listed on Google.');
      }
    } catch (error) {
      console.error('Error fetching Google ratings:', error);
      alert('Failed to fetch Google ratings. Please try again later.');
    } finally {
      setFetchingRatings(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div className="flex items-center space-x-3 md:space-x-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="p-2 hover:bg-white rounded-lg transition-colors active:scale-95 min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label="Go back"
                >
                  <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
                </button>
              )}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Provider Performance</h1>
                <p className="text-sm md:text-base text-gray-600 mt-1">Monitor provider performance and capacity metrics</p>
              </div>
            </div>
          </div>

          {/* Summary Metrics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              <div className="bg-blue-50 rounded-lg p-3 md:p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Building className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-gray-600">Providers</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-blue-600">{totalProviders}</p>
                <p className="text-xs text-gray-500 mt-1">Active providers</p>
              </div>

              <div className="bg-green-50 rounded-lg p-3 md:p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-gray-600">Quality</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-green-600">{avgDataQuality}</p>
                <p className="text-xs text-gray-500 mt-1">Avg data quality</p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-3 md:p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Star className="w-5 h-5 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-600">Rating</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-yellow-600">{avgGoogleRating}</p>
                <p className="text-xs text-gray-500 mt-1">Avg Google rating</p>
              </div>

              <div className="bg-purple-50 rounded-lg p-3 md:p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-gray-600">Total</span>
                </div>
                <p className="text-2xl md:text-3xl font-bold text-purple-600">{totalReferrals}</p>
                <p className="text-xs text-gray-500 mt-1">Referrals handled</p>
              </div>
            </div>
          </div>

          {/* Unified Search Bar with Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div className="flex items-center space-x-2">
                <Search className="w-5 h-5 md:w-6 md:h-6 text-indigo-600" />
                <h2 className="text-lg md:text-xl font-semibold text-gray-800">Search & Filter</h2>
              </div>
              <div className="flex items-center space-x-3">
                  <Button
                    onClick={fetchGoogleRatings}
                    disabled={fetchingRatings || providers.length === 0}
                    className="bg-gradient-to-r from-yellow-600 to-orange-600 text-white hover:from-yellow-700 hover:to-orange-700 hover:shadow-xl rounded-xl shadow-lg transition-all duration-300 hover:scale-105 transform disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {fetchingRatings ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Fetching...
                      </>
                    ) : (
                      <>
                        <Star className="w-5 h-5 mr-2 fill-white" />
                        Update Google Ratings
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={exportToCSV}
                    disabled={filteredProviders.length === 0}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 hover:shadow-lg rounded-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                    <span className="text-sm md:text-base">Export CSV</span>
                  </Button>
                </div>
              </div>

              {/* Search Input */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name, zip code, city, service, or organization type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 md:pl-12 pr-4 h-10 md:h-12 text-sm md:text-base border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 rounded-lg"
                />
              </div>

              {/* Time Range Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <Calendar className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                <span className="text-xs md:text-sm font-medium text-gray-700">Time Range:</span>
                {[
                  { value: 'all', label: 'All Time' },
                  { value: '30days', label: 'Last 30 Days' },
                  { value: '90days', label: 'Last 90 Days' },
                  { value: '6months', label: 'Last 6 Months' },
                  { value: '1year', label: 'Last Year' }
                ].map((range) => (
                  <button
                    key={range.value}
                    onClick={() => setDateRange(range.value as typeof dateRange)}
                    className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium rounded-lg transition-all ${
                      dateRange === range.value
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>

              {/* Results Count */}
              {searchTerm && (
                <div className="text-xs md:text-sm text-gray-600 mt-2">
                  Showing {filteredProviders.length} of {providers.length} providers
                </div>
              )}
            </div>

          {/* Provider List */}
          {loading ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-b-4 border-indigo-600"></div>
                <p className="text-sm md:text-lg text-gray-600 font-medium">Loading provider metrics...</p>
              </div>
            </div>
          ) : filteredProviders.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12">
              <div className="flex flex-col items-center justify-center space-y-4 text-center">
                <Building className="w-12 h-12 md:w-16 md:h-16 text-gray-300" />
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">No Providers Found</h3>
                <p className="text-sm md:text-base text-gray-600 max-w-md">
                  {searchTerm
                    ? "Try adjusting your search criteria"
                    : "No providers with matched referrals yet"}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {filteredProviders.map((provider) => (
                <div
                  key={provider.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedProvider(provider)}
                >
                  <div className="p-4 md:p-6">
                    {/* Provider Info */}
                    <div className="flex items-start space-x-3 md:space-x-4 mb-4">
                      <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building className="w-6 h-6 md:w-7 md:h-7 text-white" />
                      </div>
                      <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-800 mb-1">{provider.name}</h3>
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full">
                              {provider.organizationType}
                            </span>
                          </div>

                          {/* Address */}
                          {provider.streetAddress ? (
                            <div className="flex items-start space-x-2 text-sm text-gray-600 mb-1">
                              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span>{provider.streetAddress}, {provider.city}, {provider.state} {provider.zipCode}</span>
                            </div>
                          ) : (
                            <div className="flex items-start space-x-2 text-sm text-gray-400 mb-1 italic">
                              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                              <span>Address not available - Save provider to "My Providers" for full details</span>
                            </div>
                          )}

                          {/* Contact Information */}
                          <div className="flex flex-wrap gap-3 mt-2">
                            {provider.phoneNumber && (
                              <div className="flex items-center space-x-1 text-sm text-gray-600">
                                <Phone className="w-4 h-4" />
                                <span>{provider.phoneNumber}</span>
                              </div>
                            )}
                            {provider.email && (
                              <div className="flex items-center space-x-1 text-sm text-gray-600">
                                <Mail className="w-4 h-4" />
                                <span className="truncate max-w-[200px]">{provider.email}</span>
                              </div>
                            )}
                            {provider.website && (
                              <div className="flex items-center space-x-1 text-sm text-blue-600">
                                <Globe className="w-4 h-4" />
                                <a href={provider.website} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[150px]">
                                  Website
                                </a>
                              </div>
                            )}
                          </div>

                          {/* Services */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {provider.services.slice(0, 3).map((service, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded"
                              >
                                {service}
                              </span>
                            ))}
                            {provider.services.length > 3 && (
                              <span className="px-2 py-1 text-xs font-medium text-gray-500">
                                +{provider.services.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                    {/* Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mt-4">
                        {/* Referrals Accepted */}
                        <div className="bg-green-50 rounded-lg p-2 md:p-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                            <span className="text-xs md:text-sm font-medium text-gray-600">Accepted</span>
                          </div>
                          <p className="text-xl md:text-2xl font-bold text-green-600">{provider.referralsAccepted}</p>
                        </div>

                        {/* Referrals Matched */}
                        <div className="bg-blue-50 rounded-lg p-2 md:p-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                            <span className="text-xs md:text-sm font-medium text-gray-600">Matched</span>
                          </div>
                          <p className="text-xl md:text-2xl font-bold text-blue-600">{provider.referralsMatched}</p>
                        </div>

                        {/* Avg Match to Close Time */}
                        <div className="bg-indigo-50 rounded-lg p-2 md:p-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <Clock className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                            <span className="text-xs md:text-sm font-medium text-gray-600">Days</span>
                          </div>
                          <p className={`text-xl md:text-2xl font-bold ${getPerformanceColor(provider.avgMatchToCloseTime)}`}>
                            {provider.avgMatchToCloseTime > 0 ? provider.avgMatchToCloseTime : 'N/A'}
                          </p>
                        </div>

                        {/* Data Quality Score */}
                        <div className="bg-purple-50 rounded-lg p-2 md:p-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <Target className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                            <span className="text-xs md:text-sm font-medium text-gray-600">Quality</span>
                          </div>
                          <p className={`text-xl md:text-2xl font-bold ${getDataQualityColor(provider.dataQualityScore)}`}>
                            {provider.dataQualityScore}
                          </p>
                        </div>

                        {/* Google Rating */}
                        <div className="bg-yellow-50 rounded-lg p-2 md:p-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <Star className="w-4 h-4 md:w-5 md:h-5 text-yellow-600" />
                            <span className="text-xs md:text-sm font-medium text-gray-600">Rating</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <p className="text-xl md:text-2xl font-bold text-yellow-600">
                              {provider.googleRating ? provider.googleRating.toFixed(1) : 'N/A'}
                            </p>
                            {provider.googleReviewCount > 0 && (
                              <span className="text-xs text-gray-500">({provider.googleReviewCount})</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Acceptance Workflow Metrics (PHI Integration) */}
                      {(provider.acceptanceRate !== undefined || provider.phiEmailsSent !== undefined) && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mt-3 pt-3 border-t border-gray-200">
                          {/* Acceptance Rate */}
                          {provider.acceptanceRate !== undefined && (
                            <div className="bg-emerald-50 rounded-lg p-2 md:p-3">
                              <div className="flex items-center space-x-2 mb-1">
                                <CheckCircle className="w-4 h-4 text-emerald-600" />
                                <span className="text-xs font-medium text-gray-600">Accept Rate</span>
                              </div>
                              <p className="text-lg md:text-xl font-bold text-emerald-700">
                                {provider.acceptanceRate}%
                              </p>
                            </div>
                          )}

                          {/* Avg Acceptance Time */}
                          {provider.avgAcceptanceTime !== undefined && (
                            <div className="bg-sky-50 rounded-lg p-2 md:p-3">
                              <div className="flex items-center space-x-2 mb-1">
                                <Clock className="w-4 h-4 text-sky-600" />
                                <span className="text-xs font-medium text-gray-600">Avg Response</span>
                              </div>
                              <p className="text-lg md:text-xl font-bold text-sky-700">
                                {provider.avgAcceptanceTime}h
                              </p>
                            </div>
                          )}

                          {/* PHI Emails Sent */}
                          {provider.phiEmailsSent !== undefined && (
                            <div className="bg-purple-50 rounded-lg p-2 md:p-3">
                              <div className="flex items-center space-x-2 mb-1">
                                <Shield className="w-4 h-4 text-purple-600" />
                                <span className="text-xs font-medium text-gray-600">PHI Emails</span>
                              </div>
                              <p className="text-lg md:text-xl font-bold text-purple-700">
                                {provider.phiEmailsSent}
                              </p>
                            </div>
                          )}

                          {/* Pending Response */}
                          {provider.referralsPending !== undefined && provider.referralsPending > 0 && (
                            <div className="bg-orange-50 rounded-lg p-2 md:p-3">
                              <div className="flex items-center space-x-2 mb-1">
                                <AlertCircle className="w-4 h-4 text-orange-600" />
                                <span className="text-xs font-medium text-gray-600">Pending</span>
                              </div>
                              <p className="text-lg md:text-xl font-bold text-orange-700">
                                {provider.referralsPending}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Provider Detail Modal */}
      {selectedProvider && (
        <ProviderDetailModal
          provider={selectedProvider}
          onClose={() => setSelectedProvider(null)}
        />
      )}
    </div>
  );
}
